/* Infosys BPM Internal Application Access — Login Screen.
   Centred card, Infosys logo + steel-blue brand chrome, case-sensitive
   User ID + Password, confidentiality + legal notice in the footer.
   No "forgot password" / "register" — closed-set access only. */
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, Lock, ShieldAlert, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError("Please enter your User ID and Password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await login(username, password);
      // AuthProvider state-flip causes <App /> to re-render into the
      // protected routes — nothing else to do here.
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : err?.message || "Sign-in failed. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full flex items-center justify-center px-4 py-10 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 600px at 20% 0%, rgba(55,129,194,0.18) 0%, transparent 60%), radial-gradient(900px 500px at 80% 100%, rgba(11,42,74,0.20) 0%, transparent 60%), linear-gradient(180deg, #0B1B2D 0%, #0F2540 50%, #14304F 100%)",
      }}
    >
      {/* subtle grid backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative w-full max-w-[440px]">
        {/* Card */}
        <div className="rounded-2xl bg-white shadow-2xl border border-slate-200/60 overflow-hidden">
          {/* Header strip */}
          <div className="px-7 pt-7 pb-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg"
                alt="Infosys"
                className="h-7 w-auto"
                data-testid="login-infosys-logo"
              />
              <div className="h-6 w-px bg-slate-300" />
              <div className="font-display text-[13px] font-semibold tracking-tight text-slate-700">
                BPM · Intelligent Sourcing and Procurement Solution
              </div>
            </div>
            <div className="mt-5 flex items-center justify-center">
              <img
                src="/unibuy-logo.png"
                alt="UniBuy™ · Intelligent Sourcing and Procurement Solution"
                data-testid="login-unibuy-logo"
                className="h-24 sm:h-28 w-auto max-w-full object-contain select-none"
                draggable={false}
              />
            </div>
            <div className="mt-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0072CE]/10 border border-[#0072CE]/25 text-[10px] uppercase tracking-[0.2em] font-bold text-[#1F4E7D]">
              <Lock size={11} /> Restricted Access
            </div>
            <h1 className="mt-3 font-display text-[22px] font-semibold leading-tight text-slate-900 tracking-tight">
              Infosys BPM
              <br />
              Internal Application Access Only
            </h1>
            <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">
              Sign in with your Infosys-issued credentials to continue to{" "}
              <span className="font-semibold text-slate-800">UniBuy™ · Intelligent Sourcing and Procurement Solution</span>.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="px-7 py-6 space-y-4">
            <div>
              <label
                htmlFor="login-username"
                className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5"
              >
                User ID
              </label>
              <input
                id="login-username"
                data-testid="login-username-input"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={busy}
                placeholder="e.g. Infy2026BPM"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm font-mono text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0072CE]/40 focus:border-[#0072CE] transition-colors disabled:opacity-60"
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="block text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  placeholder="Case-sensitive"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 pr-10 text-sm font-mono text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0072CE]/40 focus:border-[#0072CE] transition-colors disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((x) => !x)}
                  data-testid="login-password-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  tabIndex={-1}
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <div className="mt-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400">
                User ID and password are case-sensitive
              </div>
            </div>

            {error && (
              <div
                role="alert"
                data-testid="login-error"
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800 flex items-start gap-2 leading-relaxed"
              >
                <ShieldAlert size={14} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all disabled:opacity-60"
              style={{
                background:
                  "linear-gradient(180deg, #0072CE 0%, #2E6FA8 100%)",
              }}
            >
              {busy ? (
                <>
                  <Loader2 className="animate-spin" size={14} /> Verifying…
                </>
              ) : (
                <>
                  <Lock size={13} /> Sign in to UniBuy™
                </>
              )}
            </button>
          </form>

          {/* Footer notice */}
          <div className="px-7 py-4 border-t border-slate-100 bg-slate-50">
            <div className="flex items-start gap-2 text-[10.5px] leading-relaxed text-slate-600">
              <ShieldAlert size={12} className="mt-0.5 shrink-0 text-amber-600" />
              <p>
                <span className="font-semibold text-slate-800">
                  CONFIDENTIAL · INFOSYS BPM INTERNAL.
                </span>{" "}
                This system, including all data accessed through it, is the
                property of Infosys BPM Ltd. and its authorised customers.
                Access is restricted to authorised personnel only. Unauthorised
                access, use, copying, distribution or attempted entry is
                strictly prohibited and may result in disciplinary action,
                termination of contract and civil or criminal prosecution under
                applicable laws. All activity on this platform is logged and
                monitored.
              </p>
            </div>
          </div>
        </div>

        {/* Below-card legal strip */}
        <div className="mt-5 text-center text-[10.5px] text-slate-300/85 leading-relaxed">
          © {new Date().getFullYear()} Infosys BPM Ltd. · Intelligent Sourcing and Procurement Solution · All rights reserved
          <br />
          <span className="text-slate-400/70">
            Authorised use only · Activity logged for compliance &amp; audit
          </span>
        </div>
      </div>
    </div>
  );
}
